import type { GeolocationGateway } from './geolocationGateway'
import type { GeoPoint, PermissionStatus } from './locationTypes'

/**
 * Observable UDF state of the `Location` module (spec §2a).
 *
 * `Location` is pure, domain-agnostic location: it answers exactly one
 * question — *where is the user, and may we know*. It knows nothing about
 * stations, the UK, Heathrow, or maps. The metro location-consumer
 * service (`src/state/locationConsumerService.ts`) owns all of that.
 */
export interface LocationState {
  /** Latest known OS permission status. */
  readonly permissionStatus: PermissionStatus
  /** Latest raw user coordinate, or `null` before the first fix. */
  readonly userCoordinate: GeoPoint | null
}

export const initialLocationState: LocationState = {
  permissionStatus: 'notDetermined',
  userCoordinate: null,
}

/**
 * Events the reducer accepts. Some originate from the host (button taps,
 * launch), others are fed back by the store controller after an effect
 * resolves (`PermissionChanged`, `LocationUpdated`).
 */
export type LocationEvent =
  | { kind: 'Start' }
  | { kind: 'RequestPermission' }
  | { kind: 'RequestFix' }
  | { kind: 'PermissionChanged'; status: PermissionStatus }
  | { kind: 'LocationUpdated'; point: GeoPoint }

/**
 * Side effects the store controller executes against the injected
 * `GeolocationGateway`.
 */
export type LocationEffect =
  | { kind: 'RequestSystemPermission' }
  | { kind: 'RequestFix' }
  | { kind: 'StartStream' }
  | { kind: 'ShowDeniedModal' }

export interface LocationReducerResult {
  readonly state: LocationState
  readonly effects: readonly LocationEffect[]
}

/**
 * Pure `Location` state machine. No-op events return the same `state`
 * reference (referential-equality bailout, mirroring `metroMapStore.ts`).
 */
export function locationReducer(state: LocationState, event: LocationEvent): LocationReducerResult {
  switch (event.kind) {
    case 'Start':
      return { state, effects: [{ kind: 'StartStream' }] }
    case 'RequestPermission':
      return { state, effects: [{ kind: 'RequestSystemPermission' }] }
    case 'RequestFix':
      return { state, effects: [{ kind: 'RequestFix' }] }
    case 'PermissionChanged': {
      if (state.permissionStatus === event.status) return { state, effects: [] }
      const next: LocationState = { ...state, permissionStatus: event.status }
      if (event.status === 'granted') {
        return { state: next, effects: [{ kind: 'RequestFix' }] }
      }
      if (event.status === 'denied' || event.status === 'restricted') {
        return { state: next, effects: [{ kind: 'ShowDeniedModal' }] }
      }
      return { state: next, effects: [] }
    }
    case 'LocationUpdated':
      return { state: { ...state, userCoordinate: event.point }, effects: [] }
  }
}

// --- store controller ---

/** Read-only observable handle other modules subscribe to. */
export interface LocationStore {
  getState: () => LocationState
  dispatch: (event: LocationEvent) => Promise<void>
  subscribe: (listener: () => void) => () => void
}

export interface LocationStoreDeps {
  readonly gateway: GeolocationGateway
  /** Host hook for the `ShowDeniedModal` effect. */
  readonly onShowDeniedModal?: () => void
}

/**
 * Builds a `Location` store: holds state, runs `locationReducer`, and
 * after each dispatch executes the returned effects against the injected
 * gateway, feeding their results back as further events.
 * Mirrors `createStore` in `metroMapStore.ts` (UDF store-reducer pattern).
 */
export function createLocationStore(deps: LocationStoreDeps): LocationStore {
  let state = initialLocationState
  const listeners = new Set<() => void>()

  const notify = (): void => {
    for (const l of listeners) l()
  }

  const runEffect = async (effect: LocationEffect): Promise<void> => {
    switch (effect.kind) {
      case 'RequestSystemPermission': {
        const status = await deps.gateway.permissionStatus()
        await dispatch({ kind: 'PermissionChanged', status })
        return
      }
      case 'RequestFix': {
        const point = await deps.gateway.requestFix()
        await dispatch({ kind: 'LocationUpdated', point })
        return
      }
      case 'StartStream': {
        deps.gateway.watch((point) => {
          void dispatch({ kind: 'LocationUpdated', point })
        })
        return
      }
      case 'ShowDeniedModal': {
        deps.onShowDeniedModal?.()
        return
      }
    }
  }

  const dispatch = async (event: LocationEvent): Promise<void> => {
    const result = locationReducer(state, event)
    if (result.state !== state) {
      state = result.state
      notify()
    }
    for (const effect of result.effects) {
      await runEffect(effect)
    }
  }

  return {
    getState: () => state,
    dispatch,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
